import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Loader2, MapPin, Search, ExternalLink, Leaf } from 'lucide-react';
import { API } from '../App';
import { Input } from '../components/ui/input';
import { devError } from '../utils/logger';

export default function SuppliersPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeId, setActiveId] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({}); // {user_id: marker}

  // Load suppliers
  useEffect(() => {
    axios
      .get(`${API}/suppliers/public`)
      .then((r) => setSuppliers(r.data))
      .catch((e) => devError('suppliers load', e))
      .finally(() => setLoading(false));
  }, []);

  // Initialise map once
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, {
      center: [-25, 134],   // sensible Australia-centric default
      zoom: 4,
      worldCopyJump: true,
      scrollWheelZoom: true,
      zoomControl: true,
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; OSM &copy; CARTO',
      subdomains: 'abcd',
      maxZoom: 18,
    }).addTo(map);
    mapInstanceRef.current = map;
    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // Whenever suppliers change, plot markers
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    // Clear existing markers
    Object.values(markersRef.current).forEach((m) => map.removeLayer(m));
    markersRef.current = {};

    const geocoded = suppliers.filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lng));
    if (geocoded.length === 0) return;

    geocoded.forEach((s) => {
      const accent = s.accent_color || '#C97A40';
      const icon = L.divIcon({
        className: 'patina-supplier-marker',
        html: `<div style="background:${accent};border:2px solid #1a1a1a;width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 4px 12px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;"><span style="transform:rotate(45deg);color:#fff;font-weight:700;font-size:11px;">${s.listing_count}</span></div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 28],
      });
      const popupHtml = `
        <div style="min-width:220px;font-family:'Manrope',sans-serif;color:#0F172A">
          <div style="font-family:'Playfair Display',Georgia,serif;font-size:18px;font-weight:600;margin-bottom:4px;color:#0F172A">${escapeHtml(s.company_name)}</div>
          ${s.tagline ? `<div style="font-size:12px;color:#475569;margin-bottom:6px">${escapeHtml(s.tagline)}</div>` : ''}
          ${s.location ? `<div style="font-size:11px;color:#64748B;margin-bottom:8px">📍 ${escapeHtml(s.location)}</div>` : ''}
          <div style="font-size:11px;color:#0F172A">
            <strong>${s.listing_count}</strong> item${s.listing_count === 1 ? '' : 's'} in stock
            ${s.carbon_saved_kg > 0 ? ` · 🌱 <strong>${formatCarbon(s.carbon_saved_kg)}</strong> CO₂e saved` : ''}
          </div>
          ${s.slug ? `<a href="/s/${s.slug}" style="display:inline-block;margin-top:10px;padding:6px 12px;background:${accent};color:#fff;text-decoration:none;border-radius:6px;font-size:12px;font-weight:600">Visit storefront →</a>` : ''}
        </div>`;
      const marker = L.marker([s.lat, s.lng], { icon })
        .bindPopup(popupHtml, { maxWidth: 280 })
        .addTo(map);
      marker.on('click', () => setActiveId(s.user_id));
      markersRef.current[s.user_id] = marker;
    });

    // Fit map to markers (with padding)
    const bounds = L.latLngBounds(geocoded.map((s) => [s.lat, s.lng]));
    if (geocoded.length > 1) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 9 });
    } else {
      map.setView([geocoded[0].lat, geocoded[0].lng], 11);
    }
  }, [suppliers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) =>
      [s.company_name, s.tagline, s.location, s.city, s.state, s.country]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [suppliers, search]);

  const focusSupplier = (s) => {
    setActiveId(s.user_id);
    if (s.lat && s.lng && mapInstanceRef.current) {
      mapInstanceRef.current.flyTo([s.lat, s.lng], 13, { duration: 0.7 });
      const marker = markersRef.current[s.user_id];
      if (marker) marker.openPopup();
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="suppliers-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Find suppliers</p>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-3">
            Salvage yards on the map
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            Every supplier currently selling on PatinaMart, plotted by their physical yard. Pick by
            proximity, click a pin, jump straight to their storefront.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Map */}
          <div
            ref={mapRef}
            className="w-full h-[60vh] lg:h-[72vh] rounded-xl overflow-hidden border border-border/30 bg-card/30"
            data-testid="suppliers-map"
          />

          {/* Sidebar list */}
          <aside className="space-y-3" data-testid="suppliers-list">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by name, city, country…"
                className="pl-9"
                data-testid="suppliers-search"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              {loading ? 'Loading…' : `${filtered.length} supplier${filtered.length === 1 ? '' : 's'}`}
            </p>
            <div className="space-y-2 max-h-[60vh] lg:max-h-[64vh] overflow-y-auto pr-1">
              {loading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-5 h-5 text-accent animate-spin" />
                </div>
              )}
              {!loading && filtered.length === 0 && (
                <p className="text-sm text-muted-foreground py-6 text-center">No suppliers match.</p>
              )}
              {filtered.map((s) => (
                <button
                  key={s.user_id}
                  type="button"
                  onClick={() => focusSupplier(s)}
                  className={`w-full text-left p-4 rounded-lg border transition-all ${
                    activeId === s.user_id
                      ? 'border-accent/60 bg-accent/10'
                      : 'border-border/30 bg-card/30 hover:border-accent/30'
                  }`}
                  data-testid={`supplier-item-${s.user_id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h3 className="font-playfair text-base font-semibold text-foreground">
                      {s.company_name}
                    </h3>
                    {s.slug && (
                      <Link
                        to={`/s/${s.slug}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-shrink-0 text-xs text-accent hover:underline inline-flex items-center gap-0.5"
                      >
                        Visit <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                  {s.tagline && (
                    <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{s.tagline}</p>
                  )}
                  {s.location && (
                    <p className="text-[11px] text-muted-foreground/80 inline-flex items-center gap-1 mb-2">
                      <MapPin className="w-3 h-3" /> {s.location}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                    <span className="text-foreground/80">
                      <strong>{s.listing_count}</strong> in stock
                    </span>
                    {s.carbon_saved_kg > 0 && (
                      <span className="inline-flex items-center gap-1 text-emerald-300">
                        <Leaf className="w-3 h-3" /> {formatCarbon(s.carbon_saved_kg)} CO₂e
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]),
  );
}

function formatCarbon(kg) {
  if (kg >= 1000) return `${(kg / 1000).toFixed(1)} t`;
  return `${Math.round(kg)} kg`;
}
